import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Static slot group definitions (must be inline literals for schema extraction)
// ============================================================================

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'Facebook URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'Instagram URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
};

const socialPhotoGenerator = (n: number) => ({
  prompt:
    'Pick an attractive apartment photo — a mix of interior living spaces (living rooms, kitchens, bedrooms, dining) and exterior/community shots (building exterior, pool, courtyard, amenities). Do not select bathrooms, closets, or laundry/washer-dryer shots.',
  context: `Photo ${n} of 5 in a social-feed strip on the Home page — each of the 5 photos must be a different image, and the set as a whole should feel like a varied social feed (mix interior and exterior).`,
});

const photo1Def: SlotGroupDefinition = {
  label: 'Social Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Social media photo' },
  },
  generator: socialPhotoGenerator(1),
};

const photo2Def: SlotGroupDefinition = {
  label: 'Social Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Social media photo' },
  },
  generator: socialPhotoGenerator(2),
};

const photo3Def: SlotGroupDefinition = {
  label: 'Social Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Social media photo' },
  },
  generator: socialPhotoGenerator(3),
};

const photo4Def: SlotGroupDefinition = {
  label: 'Social Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Social media photo' },
  },
  generator: socialPhotoGenerator(4),
};

const photo5Def: SlotGroupDefinition = {
  label: 'Social Photo 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Social media photo' },
  },
  generator: socialPhotoGenerator(5),
};

// ============================================================================
// Component
// ============================================================================

const photoTileClass =
  'relative block aspect-square w-full overflow-hidden bg-accent';
const photoPlaceholder = (
  <div className="flex h-full w-full items-center justify-center bg-accent text-xs uppercase tracking-wider text-muted-foreground">
    Add photo
  </div>
);

export function SocialFeedSection() {
  const facebook = useSlotGroup('socialfeedsection.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('socialfeedsection.instagram_link', instagramLinkDef);

  const photo1 = useSlotGroup('socialfeedsection.photo_1', photo1Def);
  const photo2 = useSlotGroup('socialfeedsection.photo_2', photo2Def);
  const photo3 = useSlotGroup('socialfeedsection.photo_3', photo3Def);
  const photo4 = useSlotGroup('socialfeedsection.photo_4', photo4Def);
  const photo5 = useSlotGroup('socialfeedsection.photo_5', photo5Def);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full overflow-hidden bg-background py-10 md:py-14 lg:py-16"
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 sm:px-6 lg:grid lg:grid-cols-[minmax(0,3fr)_minmax(0,9fr)] lg:items-center lg:gap-10 lg:px-8">
        {/* Left: heading + social icons */}
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
          <RichTextContent
            path="socialfeedsection.heading"
            label="Heading"
            defaultValue="<p>Follow us on social media</p>"
            as="h2"
            className="font-heading text-4xl font-bold leading-[1.05] text-primary sm:text-5xl"
          />

          <div className="mt-5 flex items-center gap-3">
            <EditableSlotGroup
              groupPath="socialfeedsection.facebook_link"
              definition={facebookLinkDef}
              className="inline-block"
            >
              <a
                href={facebook.url || '#'}
                target={facebook._target || '_blank'}
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-primary transition-opacity duration-200 hover:opacity-70"
              >
                <FontAwesomeIcon icon={faFacebookF} className="text-lg" />
              </a>
            </EditableSlotGroup>

            <EditableSlotGroup
              groupPath="socialfeedsection.instagram_link"
              definition={instagramLinkDef}
              className="inline-block"
            >
              <a
                href={instagram.url || '#'}
                target={instagram._target || '_blank'}
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-primary transition-opacity duration-200 hover:opacity-70"
              >
                <FontAwesomeIcon icon={faInstagram} className="text-lg" />
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        {/* Right: horizontal strip of 5 photos */}
        <div className="grid w-full grid-cols-2 gap-1 sm:grid-cols-3 md:grid-cols-5">
          <EditableSlotGroup
            groupPath="socialfeedsection.photo_1"
            definition={photo1Def}
            className={photoTileClass}
          >
            {photo1.image ? (
              <VibesitesImage
                url={photo1.image}
                alt={photo1.alt || 'Social media photo'}
                sizes="(min-width: 1024px) 15vw, (min-width: 768px) 20vw, (min-width: 640px) 33vw, 50vw"
                className="h-full w-full object-cover"
              />
            ) : (
              photoPlaceholder
            )}
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="socialfeedsection.photo_2"
            definition={photo2Def}
            className={photoTileClass}
          >
            {photo2.image ? (
              <VibesitesImage
                url={photo2.image}
                alt={photo2.alt || 'Social media photo'}
                sizes="(min-width: 1024px) 15vw, (min-width: 768px) 20vw, (min-width: 640px) 33vw, 50vw"
                className="h-full w-full object-cover"
              />
            ) : (
              photoPlaceholder
            )}
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="socialfeedsection.photo_3"
            definition={photo3Def}
            className={photoTileClass}
          >
            {photo3.image ? (
              <VibesitesImage
                url={photo3.image}
                alt={photo3.alt || 'Social media photo'}
                sizes="(min-width: 1024px) 15vw, (min-width: 768px) 20vw, (min-width: 640px) 33vw, 50vw"
                className="h-full w-full object-cover"
              />
            ) : (
              photoPlaceholder
            )}
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="socialfeedsection.photo_4"
            definition={photo4Def}
            className={photoTileClass}
          >
            {photo4.image ? (
              <VibesitesImage
                url={photo4.image}
                alt={photo4.alt || 'Social media photo'}
                sizes="(min-width: 1024px) 15vw, (min-width: 768px) 20vw, (min-width: 640px) 33vw, 50vw"
                className="h-full w-full object-cover"
              />
            ) : (
              photoPlaceholder
            )}
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="socialfeedsection.photo_5"
            definition={photo5Def}
            className={`${photoTileClass} hidden sm:block`}
          >
            {photo5.image ? (
              <VibesitesImage
                url={photo5.image}
                alt={photo5.alt || 'Social media photo'}
                sizes="(min-width: 1024px) 15vw, (min-width: 768px) 20vw, (min-width: 640px) 33vw, 50vw"
                className="h-full w-full object-cover"
              />
            ) : (
              photoPlaceholder
            )}
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'SocialFeedSection',
  displayName: 'Social Feed Section',
  tokens: {
    'socialfeedsection.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Follow us on social media</p>',
    },
  },
  slotGroups: {
    'socialfeedsection.facebook_link': {
      label: 'Facebook',
      fields: {
        url: {
          label: 'Facebook URL',
          type: 'url',
          defaultField: 'property_facebook_url',
          suggestedFields: ['property_facebook_url'],
        },
        _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'socialfeedsection.instagram_link': {
      label: 'Instagram',
      fields: {
        url: {
          label: 'Instagram URL',
          type: 'url',
          defaultField: 'property_instagram_url',
          suggestedFields: ['property_instagram_url'],
        },
        _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'socialfeedsection.photo_1': {
      label: 'Social Photo 1',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Social media photo' },
      },
      generator: {
        prompt:
          'Pick an attractive apartment photo — a mix of interior living spaces (living rooms, kitchens, bedrooms, dining) and exterior/community shots (building exterior, pool, courtyard, amenities). Do not select bathrooms, closets, or laundry/washer-dryer shots.',
        context:
          'Photo 1 of 5 in a social-feed strip on the Home page — each of the 5 photos must be a different image, and the set as a whole should feel like a varied social feed (mix interior and exterior).',
      },
    },
    'socialfeedsection.photo_2': {
      label: 'Social Photo 2',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Social media photo' },
      },
      generator: {
        prompt:
          'Pick an attractive apartment photo — a mix of interior living spaces (living rooms, kitchens, bedrooms, dining) and exterior/community shots (building exterior, pool, courtyard, amenities). Do not select bathrooms, closets, or laundry/washer-dryer shots.',
        context:
          'Photo 2 of 5 in a social-feed strip on the Home page — each of the 5 photos must be a different image, and the set as a whole should feel like a varied social feed (mix interior and exterior).',
      },
    },
    'socialfeedsection.photo_3': {
      label: 'Social Photo 3',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Social media photo' },
      },
      generator: {
        prompt:
          'Pick an attractive apartment photo — a mix of interior living spaces (living rooms, kitchens, bedrooms, dining) and exterior/community shots (building exterior, pool, courtyard, amenities). Do not select bathrooms, closets, or laundry/washer-dryer shots.',
        context:
          'Photo 3 of 5 in a social-feed strip on the Home page — each of the 5 photos must be a different image, and the set as a whole should feel like a varied social feed (mix interior and exterior).',
      },
    },
    'socialfeedsection.photo_4': {
      label: 'Social Photo 4',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Social media photo' },
      },
      generator: {
        prompt:
          'Pick an attractive apartment photo — a mix of interior living spaces (living rooms, kitchens, bedrooms, dining) and exterior/community shots (building exterior, pool, courtyard, amenities). Do not select bathrooms, closets, or laundry/washer-dryer shots.',
        context:
          'Photo 4 of 5 in a social-feed strip on the Home page — each of the 5 photos must be a different image, and the set as a whole should feel like a varied social feed (mix interior and exterior).',
      },
    },
    'socialfeedsection.photo_5': {
      label: 'Social Photo 5',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Social media photo' },
      },
      generator: {
        prompt:
          'Pick an attractive apartment photo — a mix of interior living spaces (living rooms, kitchens, bedrooms, dining) and exterior/community shots (building exterior, pool, courtyard, amenities). Do not select bathrooms, closets, or laundry/washer-dryer shots.',
        context:
          'Photo 5 of 5 in a social-feed strip on the Home page — each of the 5 photos must be a different image, and the set as a whole should feel like a varied social feed (mix interior and exterior).',
      },
    },
  },
};

(SocialFeedSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SocialFeedSection;
